import { AppointmentStatus } from './status';
import { AppointmentType } from './appointment';
import { ExportableCustomFormsFeatures } from './custom-forms';

export enum ReportSearchDefinedRanges {
  TODAY = 'today',
  WTD = 'wtd',
  MTD = 'mtd',
  YTD = 'ytd'
}

export interface IReportSearches {
  [titleSlug: string]: IReportSearch;
}

export interface IReportSearch {
  title: string;
  createdAt?: Date;
  filters: IReportSearchFilters;
}

export interface IReportSearchFilters {
  toDate?: Date;
  fromDate?: Date;
  dateField: ReportDateFields;
  warehouseIds: string[];
  loadTypeIds?: string[];
  dockIds?: string[];
  appointmentCreatorTypes?: AppointmentCreatorTypeReportOptions[];
  carrierIds?: string[];
  exportFields: ExportableFieldsType[];
  statuses: AppointmentStatus[];
  definedRange?: ReportSearchDefinedRanges;
  tags: string[];
  appointmentTypes: AppointmentType[];
  allCarriers: boolean;
}

export function definedRangeMetadata(range: ReportSearchDefinedRanges) {
  return {
    [ReportSearchDefinedRanges.TODAY]: {
      description: 'Appointments Scheduled for Today',
      label: 'Today',
      startOf: 'day'
    },
    [ReportSearchDefinedRanges.WTD]: {
      description: 'Week to Date Starting on Sunday',
      label: 'Week to Date',
      startOf: 'week'
    },
    [ReportSearchDefinedRanges.MTD]: {
      description: 'Month to Date Starting on the 1st',
      label: 'Month to Date',
      startOf: 'month'
    },
    [ReportSearchDefinedRanges.YTD]: {
      description: 'Year to Date Starting January 1st',
      label: 'Year to Date',
      startOf: 'year'
    }
  }[range];
}

enum RegularExportableFields {
  CONFIRMATION_NUMBER = 'Confirmation #',
  APPOINTMENT_ID = 'Appointment ID',
  APPOINTMENT_DATE = 'Appt Date',
  APPOINTMENT_TIME = 'Appt Time',
  APPOINTMENT_TIMEZONE = 'Timezone',
  APPOINTMENT_TYPE = 'Appt Type',
  SCHEDULED_DURATION = 'Scheduled Duration (mins)',
  STATUS = 'Status',
  ARRIVAL_DATE = 'Arrival Date',
  ARRIVAL_TIME = 'Arrival Time',
  DEPARTURE_DATE = 'Departure Date',
  DEPARTURE_TIME = 'Departure Time',
  DWELL_TIME = 'Dwell Time (mins)',
  ON_TIME = 'On-Time (mins)',
  IN_PROGRESS_DATE = 'In Progress date', //the date of when the appt was marked as In Progress
  IN_PROGRESS_TIME = 'In Progress time', //the time of when the appt was marked as In Progress
  WAIT_TIME = 'Wait time (mins)', //the time that elapses between Arrival time and when the app is marked as In Progress
  PROCESSING_TIME = 'Processing time (mins)', // the time that elapses between In Progress time and when the appt is marked as Completed
  WAREHOUSE = 'Warehouse',
  DOCK = 'Dock',
  LOAD_TYPE = 'Load Type',
  LOAD_TYPE_DIRECTION = 'Direction',
  RESCHEDULE_COUNT = 'Reschedule Count',
  REFERENCE_NUMBER = 'Reference Number',
  HANDLING_UNITS = 'Handling Units',
  CREATED_BY = 'Created By',
  CREATOR_TYPE = 'Creator Type',
  CREATION_DATE = 'Creation Date',
  LAST_UPDATED_BY = 'Last Updated By',
  LAST_UPDATE_DATE = 'Last Update Date',
  LAST_UPDATE_TIME = 'Last Update Time',
  CARRIER_COMPANY = 'Carrier Company',
  CONTACT_NAME = 'Contact Name',
  CONTACT_EMAIL = 'Contact Email',
  CONTACT_PHONE = 'Contact Phone',
  NOTES = 'Notes',
  TAGS = 'Tags',
  CUSTOM_FIELDS = 'Custom Fields',
  EXTERNAL_VALIDATION_STATUS = 'External Validation Status',
  EXTERNAL_VALIDATION_ERROR_MESSAGE = 'External Validation Error Message'
}

export const ExportableFields = { ...RegularExportableFields, ...ExportableCustomFormsFeatures };
export type ExportableFieldsType = RegularExportableFields | ExportableCustomFormsFeatures;

type ExportableFieldKey = keyof typeof ExportableFields;

const ExportableFieldLabelsByLocale: Record<string, Partial<Record<ExportableFieldKey, string>>> = {
  'pt-br': {
    CONFIRMATION_NUMBER: 'Confirmação #',
    APPOINTMENT_ID: 'ID do agendamento',
    APPOINTMENT_DATE: 'Data do agendamento',
    APPOINTMENT_TIME: 'Hora do agendamento',
    APPOINTMENT_TIMEZONE: 'Fuso horário',
    APPOINTMENT_TYPE: 'Tipo de agendamento',
    SCHEDULED_DURATION: 'Duração agendada (min)',
    STATUS: 'Status',
    ARRIVAL_DATE: 'Data de chegada',
    ARRIVAL_TIME: 'Hora de chegada',
    DEPARTURE_DATE: 'Data de saída',
    DEPARTURE_TIME: 'Hora de saída',
    DWELL_TIME: 'Tempo de permanência (min)',
    ON_TIME: 'Pontualidade (min)',
    IN_PROGRESS_DATE: 'Data de início (em andamento)',
    IN_PROGRESS_TIME: 'Hora de início (em andamento)',
    WAIT_TIME: 'Tempo de espera (min)',
    PROCESSING_TIME: 'Tempo de processamento (min)',
    WAREHOUSE: 'Armazém',
    DOCK: 'Doca',
    LOAD_TYPE: 'Tipo de carga',
    LOAD_TYPE_DIRECTION: 'Direção',
    RESCHEDULE_COUNT: 'Número de reagendamentos',
    REFERENCE_NUMBER: 'Número de referência',
    HANDLING_UNITS: 'Unidades de manuseio',
    CREATED_BY: 'Criado por',
    CREATOR_TYPE: 'Tipo de criador',
    CREATION_DATE: 'Data de criação',
    LAST_UPDATED_BY: 'Última atualização por',
    LAST_UPDATE_DATE: 'Data da última atualização',
    LAST_UPDATE_TIME: 'Hora da última atualização',
    CARRIER_COMPANY: 'Transportadora',
    CONTACT_NAME: 'Nome do contato',
    CONTACT_EMAIL: 'E-mail do contato',
    CONTACT_PHONE: 'Telefone do contato',
    NOTES: 'Notas',
    TAGS: 'Tags',
    CUSTOM_FIELDS: 'Campos personalizados',
    EXTERNAL_VALIDATION_STATUS: 'Status de validação externa',
    EXTERNAL_VALIDATION_ERROR_MESSAGE: 'Mensagem de erro de validação externa',
    CheckOut: 'Saída',
    CheckInAdditionalAssetDetails: 'Detalhes adicionais do ativo na entrada',
    InboundClaim: 'Reclamação de entrada',
    OutboundClaim: 'Reclamação de saída',
    BookLoadType: 'Tipo de carga do agendamento',
    AssetContainerDetails: 'Detalhes do contêiner do ativo',
    AppointmentCompleted: 'Agendamento concluído'
  },
  'es-mx': {
    CONFIRMATION_NUMBER: 'Confirmación #',
    APPOINTMENT_ID: 'ID de la cita',
    APPOINTMENT_DATE: 'Fecha de la cita',
    APPOINTMENT_TIME: 'Hora de la cita',
    APPOINTMENT_TIMEZONE: 'Zona horaria',
    APPOINTMENT_TYPE: 'Tipo de cita',
    SCHEDULED_DURATION: 'Duración programada (min)',
    STATUS: 'Estado',
    ARRIVAL_DATE: 'Fecha de llegada',
    ARRIVAL_TIME: 'Hora de llegada',
    DEPARTURE_DATE: 'Fecha de salida',
    DEPARTURE_TIME: 'Hora de salida',
    DWELL_TIME: 'Tiempo de permanencia (min)',
    ON_TIME: 'Puntualidad (min)',
    IN_PROGRESS_DATE: 'Fecha de inicio (en progreso)',
    IN_PROGRESS_TIME: 'Hora de inicio (en progreso)',
    WAIT_TIME: 'Tiempo de espera (min)',
    PROCESSING_TIME: 'Tiempo de procesamiento (min)',
    WAREHOUSE: 'Almacén',
    DOCK: 'Muelle',
    LOAD_TYPE: 'Tipo de carga',
    LOAD_TYPE_DIRECTION: 'Dirección',
    RESCHEDULE_COUNT: 'Número de reprogramaciones',
    REFERENCE_NUMBER: 'Número de referencia',
    HANDLING_UNITS: 'Unidades de manipulación',
    CREATED_BY: 'Creado por',
    CREATOR_TYPE: 'Tipo de creador',
    CREATION_DATE: 'Fecha de creación',
    LAST_UPDATED_BY: 'Última actualización por',
    LAST_UPDATE_DATE: 'Fecha de la última actualización',
    LAST_UPDATE_TIME: 'Hora de la última actualización',
    CARRIER_COMPANY: 'Empresa transportista',
    CONTACT_NAME: 'Nombre de contacto',
    CONTACT_EMAIL: 'Correo de contacto',
    CONTACT_PHONE: 'Teléfono de contacto',
    NOTES: 'Notas',
    TAGS: 'Etiquetas',
    CUSTOM_FIELDS: 'Campos personalizados',
    EXTERNAL_VALIDATION_STATUS: 'Estado de validación externa',
    EXTERNAL_VALIDATION_ERROR_MESSAGE: 'Mensaje de error de validación externa',
    CheckOut: 'Salida',
    CheckInAdditionalAssetDetails: 'Detalles adicionales del activo en el check-in',
    InboundClaim: 'Reclamación de entrada',
    OutboundClaim: 'Reclamación de salida',
    BookLoadType: 'Tipo de carga de la cita',
    AssetContainerDetails: 'Detalles del contenedor del activo',
    AppointmentCompleted: 'Cita completada'
  },
  'fr-ca': {
    CONFIRMATION_NUMBER: 'No de confirmation',
    APPOINTMENT_ID: 'ID du rendez-vous',
    APPOINTMENT_DATE: 'Date du rendez-vous',
    APPOINTMENT_TIME: 'Heure du rendez-vous',
    APPOINTMENT_TIMEZONE: 'Fuseau horaire',
    APPOINTMENT_TYPE: 'Type de rendez-vous',
    SCHEDULED_DURATION: 'Durée planifiée (min)',
    STATUS: 'Statut',
    ARRIVAL_DATE: "Date d'arrivée",
    ARRIVAL_TIME: "Heure d'arrivée",
    DEPARTURE_DATE: 'Date de départ',
    DEPARTURE_TIME: 'Heure de départ',
    DWELL_TIME: 'Temps de séjour (min)',
    ON_TIME: 'Ponctualité (min)',
    IN_PROGRESS_DATE: 'Date de début (en cours)',
    IN_PROGRESS_TIME: 'Heure de début (en cours)',
    WAIT_TIME: "Temps d'attente (min)",
    PROCESSING_TIME: 'Temps de traitement (min)',
    WAREHOUSE: 'Entrepôt',
    DOCK: 'Quai',
    LOAD_TYPE: 'Type de chargement',
    LOAD_TYPE_DIRECTION: 'Direction',
    RESCHEDULE_COUNT: 'Nombre de replanifications',
    REFERENCE_NUMBER: 'Numéro de référence',
    HANDLING_UNITS: 'Unités de manutention',
    CREATED_BY: 'Créé par',
    CREATOR_TYPE: 'Type de créateur',
    CREATION_DATE: 'Date de création',
    LAST_UPDATED_BY: 'Dernière mise à jour par',
    LAST_UPDATE_DATE: 'Date de la dernière mise à jour',
    LAST_UPDATE_TIME: 'Heure de la dernière mise à jour',
    CARRIER_COMPANY: 'Entreprise de transport',
    CONTACT_NAME: 'Nom du contact',
    CONTACT_EMAIL: 'Courriel du contact',
    CONTACT_PHONE: 'Téléphone du contact',
    NOTES: 'Notes',
    TAGS: 'Étiquettes',
    CUSTOM_FIELDS: 'Champs personnalisés',
    EXTERNAL_VALIDATION_STATUS: 'Statut de validation externe',
    EXTERNAL_VALIDATION_ERROR_MESSAGE: "Message d'erreur de validation externe",
    CheckOut: 'Sortie',
    CheckInAdditionalAssetDetails: "Détails supplémentaires de l'actif à l'arrivée",
    InboundClaim: "Réclamation d'entrée",
    OutboundClaim: 'Réclamation de sortie',
    BookLoadType: 'Type de chargement du rendez-vous',
    AssetContainerDetails: "Détails du conteneur de l'actif",
    AppointmentCompleted: 'Rendez-vous terminé'
  }
};

function normalizeExportLocale(locale?: string | null): string {
  if (!locale) {
    return 'en';
  }

  const normalizedLocale = locale.toLowerCase();
  if (normalizedLocale.startsWith('pt')) {
    return 'pt-br';
  }
  if (normalizedLocale.startsWith('es')) {
    return 'es-mx';
  }
  if (normalizedLocale.startsWith('fr')) {
    return 'fr-ca';
  }

  return 'en';
}

export function getExportableFieldLabel(field: ExportableFieldKey, locale?: string | null): string {
  const normalizedLocale = normalizeExportLocale(locale);
  const localizedFieldLabels = ExportableFieldLabelsByLocale[normalizedLocale];
  return localizedFieldLabels?.[field] ?? ExportableFields[field];
}

export enum ReportDateFields {
  APPOINTMENT_DATE = 'Appointment Date',
  CREATION_DATE = 'Creation Date'
}

export enum AssetVisitReportDateFields {
  CheckInDate = 'checkInDate',
  CheckOutDate = 'checkOutDate'
}

export enum AssetVisitCustomFormCategory {
  CheckIn = 'Check-in',
  CheckOut = 'Check-out',
  AssetDetails = 'Asset Details'
}

export type AssetVisitCustomFormExportRow = {
  category: AssetVisitCustomFormCategory;
  containerIndex: number | null;
  label: string;
  value: string;
  type: string;
};

/**
 * Labels for check-in / asset-visit Excel export column picker.
 * Keys must match raw SQL aliases from Neutron `AssetVisitMetricsService.buildAssetVisitQuery`
 * and `POST /metrics/asset-visits/excel` `exportFields`.
 */
export const AssetVisitExcelExportFields = {
  id: 'Visit ID',
  appointmentId: 'Appointment ID',
  checkInDate: 'Check-in Date/Time',
  checkOutDate: 'Check-out Date/Time',
  carrier: 'Carrier',
  warehouseName: 'Warehouse',
  currentStatus: 'Status',
  licensePlate: 'License Plate',
  visitType: 'Visit Type',
  dotNumber: 'DOT Number',
  phone: 'Phone',
  isPlanned: 'Planned',
  driverNotes: 'Driver Notes',
  checkInAcknowledged: 'Check-in Acknowledged',
  firstName: 'ID Verification First Name',
  lastName: 'ID Verification Last Name',
  [AssetVisitCustomFormCategory.CheckIn]: 'Check-in Form',
  [AssetVisitCustomFormCategory.CheckOut]: 'Check-out Form',
  [AssetVisitCustomFormCategory.AssetDetails]: 'Asset Details Form'
} as const;

export type AssetVisitExcelExportFieldKey = keyof typeof AssetVisitExcelExportFields;

export enum AssetVisitStatus {
  Arrived = 'Arrived',
  Docked = 'Docked',
  Departed = 'Departed',
  Canceled = 'Canceled',
  RejectedByCheckin = 'Rejected by Check-in',
  RejectedByWarehouse = 'Rejected by Warehouse'
}

export enum AppointmentCreatorTypeReportOptions {
  WAREHOUSE_USER = 'Warehouse User',
  CARRIER = 'Carrier'
}

export enum WarehouseMetricGroupOptions {
  WAREHOUSE = 'warehouse',
  DOCK = 'dock',
  LOAD_TYPE = 'loadType',
  DIRECTION = 'direction'
}

export enum ExportableYardFields {
  ASSET_ID = 'Asset ID',
  ASSET_TYPE = 'Asset Type',
  CARRIER_COMPANY = 'Carrier Company',
  CARRIER_VISIT_TYPE = 'Carrier Visit Type',
  SPOT = 'Spot',
  SPOT_AREA = 'Area',
  ARRIVAL_DATE = 'Arrival Date',
  ARRIVAL_TIME = 'Arrival Time',
  DEPARTURE_DATE = 'Departure Date',
  DEPARTURE_TIME = 'Departure Time',
  TIME_IN_YARD = 'Time in the Yard',
  ASSET_OWNER = 'Asset Owner',
  ASSET_STATE = 'Asset State',
  PLANNED_ARRIVAL = 'Planned Arrival',
  DRIVERS_PHONE_NUMBER = "Driver's Phone Number",
  REFERENCE_NUMBER = 'Reference Number',
  CONFIRMATION_NUMBER = 'Confirmation Number',
  DRIVER_APPOINTMENT_IDENTIFIER = 'Driver Appointment Identifier',
  DROPOFF_CARRIER_COMPANY = 'Dropoff Carrier Company',
  PICKUP_CARRIER_COMPANY = 'Pickup Carrier Company',
  CHECK_IN_FORM = 'Check-in form',
  ASSET_DETAILS_FORM = 'Asset Details form',
  OPEN_MOVE_TASK = 'Open Move Task',
  ASSET_STATUS = 'Asset Status',
  DROPOFF_REFERENCE_NUMBER = 'Dropoff Reference Number',
  PICKUP_REFERENCE_NUMBER = 'Pickup Reference Number',
  LOADED_DATE = 'Loaded Date',
  LOADED_TIME = 'Loaded Time',
  NOTES = 'Notes'
}
